import * as React from 'react';

import { Form } from 'react-bootstrap';

import dateFormat from 'dateformat';
import Loader from '../Loader';
import LoaderWheel from '../LoaderWheel';
import { Utilities } from '../../../MedicareProAPI/Utilities/Utilities';
import StyledModal from '../StyledModal';
import { Manual } from './Manual';
import { SoaForm } from './SoaForm';
import { ConsentAForm } from './ConsentAForm';
import { ConsentBForm } from './ConsentBForm';
import { UserSendEmailSelection } from '../UserSendEmailSelection';
import { UserSendSmsSelection } from '../UserSendSmsSelection';
import { UserSoa } from '../../../MedicareProAPI/Entities/User/UserSoa';
import type { User } from '../../../MedicareProAPI/Entities/User/User';
import type { Ind } from '../../../MedicareProAPI/Entities/Ind/Ind';
import { UserSoaRepository } from '../../../MedicareProAPI/Repositories/User/UserSoaRepository';
import type { IndAPIInclude } from '../../../MedicareProAPI/Repositories/Ind/IndRepository';
import { IndRepository } from '../../../MedicareProAPI/Repositories/Ind/IndRepository';
import type { UserSoaStatus } from '../../../MedicareProAPI/Entities/User/UserSoaStatus';
import { UserRepository } from '../../../MedicareProAPI/Repositories/User/UserRepository';
import type { IndPolicy } from '../../../MedicareProAPI/Entities/Ind/IndPolicy';
import { FileUploadResponse, WebAPI } from '../../../MedicareProAPI/API/WebAPI';
import type { IndDocument } from '../../../MedicareProAPI/Entities/Ind/IndDocument';
import { AgencyDownlineRepository } from '../../../MedicareProAPI/Repositories/Agency/AgencyDownlineRepository';
import type { INTEGRATION_TYPE } from '../../Constants/Integrations';
import { INTEGRATION_OPTIONS } from '../../Constants/Integrations';

/**
 * Modal for completing a CMS form
 *
 * @author Andrew Brown <abrown@medicareprocrm.com>
 */
export interface AgentIndCmsFormModalProps {
  formTypeSelected: string;
  props: any;
  onClose: () => void;
  onSave: (indId: number, formData: any) => Promise<UserSoa>;
  //onUpdate : (formData: any) => void;
  onEmailSend: (data: any, userSoa: UserSoa, individual: Ind, agent: User) => Promise<string>;
  onSmsSend: (data: any, soa: UserSoa, agent: User) => Promise<string>;
  onFormPrintF2f: (soa: UserSoa) => Promise<string>;
  onSaveFormStatus: (message: string, soaId: number) => Promise<UserSoaStatus>;
  onDocUpload?: (indId: number, fileData: any) => Promise<IndDocument | null>;
  agencyId?: number;
  onIntegrationSetupClick?: (serviceType: INTEGRATION_TYPE) => void;
}
/**
 * Modal for completing a CMS form
 *
 * @author Andrew Brown <abrown@medicareprocrm.com>
 */
export const AgentIndCmsFormModal = (props: AgentIndCmsFormModalProps): React.JSX.Element => {
  console.log('props', props);
  const userSoaRepository = new UserSoaRepository();
  userSoaRepository.setAccessToken(props.props.userAccessToken);

  const userRepository = new UserRepository();
  userRepository.setAccessToken(props.props.userAccessToken);

  const agencyDownlineRepository = new AgencyDownlineRepository();
  agencyDownlineRepository.setAccessToken(props.props.userAccessToken);

  const [individual, setInd] = React.useState<Ind>();
  const [newestPolicy, setNewestPolicy] = React.useState<IndPolicy>();
  const [agents, setAgents] = React.useState<Array<User>>();
  const [selectedAgent, setSelectedAgent] = React.useState<User>();
  const [sendFailureMessage, setSendFailureMessage] = React.useState<string>(null);
  const [formData, setFormData] = React.useState<any>();
  const [sendingData, setSendingData] = React.useState<any>();
  const [savedForm, setSavedForm] = React.useState<boolean>(false);
  const [formIsSaving, setFormIsSaving] = React.useState<boolean>(false);
  const [formModalShow, setFormModalShow] = React.useState<boolean>(true);
  const [formIsValid, setFormIsValid] = React.useState<boolean>(false);
  const [sendingMethodIsValid, setSendingMethodIsValid] = React.useState<boolean>(
    props.formTypeSelected === 'manual' ? true : false
  );
  const [formWasCreated, setFormWasCreated] = React.useState<boolean>(false);
  const formTypeSelected = props.formTypeSelected;
  const [formDocAttachment, setFormDocAttachment] = React.useState<any>();
  const [uploadError, setUploadError] = React.useState<boolean>(false);

  //const date = new Date(new Date().getTime()+(2*24*60*60*1000));
  const date = new Date();
  const month = date.getMonth() + 1;
  const appointmentDate =
    date.getFullYear() + '-' + month + '-' + String(date.getDate()).padStart(2, '0');

  const rdate = new Date();
  const rdateMonth = rdate.getMonth() + 1;
  const reviewDate =
    date.getFullYear() + '-' + rdateMonth + '-' + String(rdate.getDate()).padStart(2, '0');

  const visitortimezone = rdate.getTimezoneOffset() / 60;

  const typeLegend = [
    {
      language: 'ENG',
      value: 'soa',
      label: 'SOA',
      emailTemplateName: 'ClientSoaSend',
      subject: 'Scope of Appointment',
    },
    {
      language: 'ENG',
      value: 'consentA',
      label: 'Consent A',
      emailTemplateName: 'ClientConsentASend',
      subject: 'Consumer Consent Form A',
    },
    {
      language: 'ENG',
      value: 'consentB',
      label: 'Consent B',
      emailTemplateName: 'ClientConsentBSend',
      subject: 'Consumer Consent Form B',
    },
    {
      language: 'ESP',
      value: 'soa',
      label: 'SOA',
      emailTemplateName: 'ClientSoaSend_ESP',
      subject: 'Alcance del Formulario de Confirmación de Cita',
    },
    {
      language: 'ESP',
      value: 'consentA',
      label: 'Consent A',
      emailTemplateName: 'ClientConsentASend_ESP',
      subject: 'Consumer Consent Form A',
    },
    {
      language: 'ESP',
      value: 'consentB',
      label: 'Consent B',
      emailTemplateName: 'ClientConsentBSend_ESP',
      subject: 'Consumer Consent Form B',
    },
    { language: '', value: 'manual', label: 'Manual Document', emailTemplateName: '', subject: '' },
  ];

  const sendingOptions = [
    { label: 'Email', value: 'email' },
    { label: 'SMS', value: 'sms' },
    { label: 'Print', value: 'print' },
    { label: 'Face-to-Face', value: 'f2f' },
  ];

  const languageOptions = [
    { label: 'English', value: 'ENG' },
    { label: 'Spanish', value: 'ESP' },
  ];

  const handleIntegrationSetup = (serviceType: INTEGRATION_TYPE) => {
    props.onClose();
    props.onIntegrationSetupClick(serviceType);
  };

  /**
   * Handles form changes
   * @param {React.SyntheticEvent} event
   */
  const handleFormChange = (event) => {
    const name = event.target.name;
    let value = event.target.value;

    if (name == 'soaDiscussionCheck') {
      const soaDiscussionCheck = formData.soaDiscussionCheck;
      const isChecked = event.target.checked;
      let _soaDiscussionCheck;

      if (isChecked) {
        _soaDiscussionCheck = [...soaDiscussionCheck, value];
      } else {
        _soaDiscussionCheck = soaDiscussionCheck.filter((item) => item !== value);
      }
      value = _soaDiscussionCheck;
    }

    if (name == 'soaTransmissionMethod' && ['print', 'f2f'].includes(value)) {
      handleSendingMethodSelections({ method: 'print', to: 'print' });
    }

    if (name == 'soaNoAuth') {
      const isChecked = event.target.checked;

      if (isChecked) {
        value = false;
        localStorage.removeItem('soaNoAuth');
      } else {
        value = true;
        localStorage.setItem('soaNoAuth', 'true');
      }
    }

    const _formData = { ...formData, [name]: value };
    console.log(_formData);
    setFormData(_formData);
  };

  /**
   * Handles validating form
   */
  const handleFormValidation = () => {
    let formIsValid = true;

    let requiredFields = [];

    if (formTypeSelected !== 'manual') {
      requiredFields = [
        'soaBeneficiaryName',
        'soaBeneficiaryPhone',
        'soaBeneficiaryAddress',
        'soaAgentName',
        'soaAgentPhone',
        'soaAgentSignature',
        'soaInitialContactMethod',
        'soaAppointmentDate',
        'soaDiscussedPlans',
        'soaDiscussionCheck',
      ];
    } else {
      requiredFields = ['externalName', 'soaCreatedDate', 'isConsent', 'soaState'];
    }

    for (const [key, value] of Object.entries(formData)) {
      if (requiredFields.includes(key) && value == '') {
        formIsValid = false;
      }
    }

    return formIsValid;
  };

  /**
   * Handles saving the form
   * @param {React.SyntheticEvent} event
   */
  const handleFormSave = (formData) => {
    setFormIsSaving(true);

    const _formData = { ...formData };
    _formData.soaDiscussionCheck = formData.soaDiscussionCheck.join(',');

    const userSoa = new UserSoa();
    userSoa
      .setSoaBeneficiaryName(_formData.soaBeneficiaryName)
      .setSoaBeneficiaryPhone(_formData.soaBeneficiaryPhone)
      .setSoaBeneficiaryAddress(_formData.soaBeneficiaryAddress)
      .setSoaAgentName(_formData.soaAgentName)
      .setSoaAgentPhone(_formData.soaAgentPhone)
      .setSoaAgentNpn(_formData.soaAgentNpn)
      //.setSoaAgentSignature(_formData.soaAgentSignature)
      .setSoaInitialContactMethod(_formData.soaInitialContactMethod)
      .setSoaAppointmentDate(_formData.soaAppointmentDate)
      .setSoaDiscussedPlans(_formData.soaDiscussedPlans)
      .setSoaDiscussionCheck(_formData.soaDiscussionCheck)
      .setSoaTimezoneOffset(_formData.soaTimezoneOffset)
      .setSoaLanguage(_formData.soaLanguage)
      .setIsConsent(_formData.isConsent)
      .setSoaTransmissionMethod(_formData.soaTransmissionMethod)
      .setSoaTransmissionAddress(_formData.soaTransmissionAddress)
      .setSoaIncomeYear(_formData.soaIncomeYear)
      .setSoaReviewDate(_formData.soaReviewDate)
      .setSoaIncome(_formData.soaIncome)
      .setConsentCompany(_formData.consentCompany)
      .setUser(_formData.user)
      .setSoaNoAuth(_formData.soaNoAuth);

    _formData.consentCompany ? userSoa.setConsentCompany(_formData.consentCompany) : '';
    _formData.consentEffective ? userSoa.setConsentEffective(_formData.consentEffective) : '';
    _formData.consentPlan ? userSoa.setConsentPlan(_formData.consentPlan) : '';
    _formData.consentSubsidy ? userSoa.setConsentSubsidy(_formData.consentSubsidy) : '';
    _formData.consentPremium ? userSoa.setConsentPremium(_formData.consentPremium) : '';

    // if printing set the agent signature for ease
    _formData.soaTransmissionMethod == 'print'
      ? userSoa.setSoaAgentSignature(_formData.soaAgentName)
      : '';

    // Manual
    if (formTypeSelected === 'manual') {
      userSoa.setExternal(1);
      _formData.externalName ? userSoa.setExternalName(_formData.externalName) : '';
      _formData.soaExternalSrc ? userSoa.setSoaExternalSrc(_formData.soaExternalSrc) : '';
      userSoa.setSoaTransmissionMethod('manual');
      _formData.otherDocName ? userSoa.setOtherDocName(_formData.otherDocName) : '';
      _formData.soaState ? userSoa.setSoaState(_formData.soaState) : '';
      _formData.soaCreatedDate ? userSoa.setSoaCreatedDate(_formData.soaCreatedDate) : '';
    }

    // Save the form
    props.onSave(individual.getIndId(), userSoa).then((newForm) => {
      const statusMessage =
        'Form created & signed by agent' + '. Access Code: ' + newForm.getCode();

      // save the status
      props.onSaveFormStatus(statusMessage, newForm.getSoaId()).then((response) => {
        let sendingResponse;

        if (newForm.getSoaTransmissionMethod() === 'email') {
          sendingResponse = props.onEmailSend(sendingData, newForm, individual, selectedAgent);
        }
        if (newForm.getSoaTransmissionMethod() === 'sms') {
          sendingResponse = props.onSmsSend(sendingData, newForm, selectedAgent);
        }
        if (['print', 'f2f'].includes(newForm.getSoaTransmissionMethod())) {
          sendingResponse = props.onFormPrintF2f(newForm);
        }

        setFormIsSaving(false);
        setFormWasCreated(true);

        if (sendingResponse) {
          sendingResponse.then((response) => {
            if (['print', 'f2f'].includes(newForm.getSoaTransmissionMethod())) {
              setFormModalShow(false);
            } else {
              if ('id' in response) {
                if (response.id === '') {
                  setSendFailureMessage(JSON.stringify(response.response));
                } else {
                  props.onClose;
                  setFormModalShow(false);
                }
              } else {
                if (typeof response === 'object') {
                  props.onClose;
                  setFormModalShow(false);
                } else {
                  setSendFailureMessage(JSON.stringify(response));
                }
              }
            }
          });
        } else {
          setFormModalShow(false);
        }
      });
    });
  };

  /**
   * Handles selection of how form will be sent
   * @param {any} sendingMethod
   */
  const handleSendingMethodSelections = (sendingMethod) => {
    setSendingData(sendingMethod);

    const _formData = { ...formData };

    if (sendingMethod.to) {
      _formData.soaTransmissionAddress = sendingMethod.to;
    }

    setFormData(_formData);

    if (['print', 'f2f'].includes(sendingMethod.method)) {
      setSendingMethodIsValid(true);
    }
  };

  /**
   * Handles sending validation
   * @param {boolean} isValid
   */
  const handleSendingMethodIsValid = (isValid) => {
    setSendingMethodIsValid(isValid);

    if (isValid) {
      setSendFailureMessage(null);
    }
  };

  /**
   * Handles changing the agent on the form
   * @param {React.SyntheticEvent} event
   */
  const handleAgentChange = (event) => {
    const index = event.target.value;

    const _formData = { ...formData };

    const agent = agents[index];
    const agentName = agent.getUserFirstname() + ' ' + agent.getUserLastname();
    const agentPhone = agent.getUserPhone();
    const agentNpn = agent.getNpn() ? agent.getNpn().getNpn() : '';

    _formData['soaAgentName'] = agentName;
    _formData['soaAgentPhone'] = agentPhone;
    _formData['soaAgentNpn'] = agentNpn;
    _formData['soaAgentSignature'] = agentName;
    _formData['soaTransmissionMethod'] = null;
    _formData['soaTransmissionAddress'] = '';
    _formData['user'] = agent.getUserId();

    setFormData(_formData);
    setSelectedAgent(agent);
  };

  const handleMakeFormData = (individual, selectedAgent, newestPolicy) => {
    console.log('app date:', appointmentDate);
    const formData = {
      soaBeneficiaryName: individual.getIndFirstname() + ' ' + individual.getIndLastname(),
      soaBeneficiaryPhone: individual.getAnyPhone()
        ? individual.getAnyPhone().getIndPhoneNumber()
        : '',
      soaBeneficiaryAddress: individual.getAnyAddress() ? individual.getAnyAddressConcat() : '',
      soaAgentName: selectedAgent.getUserFirstname() + ' ' + selectedAgent.getUserLastname(),
      soaAgentPhone: selectedAgent.getUserPhone(),
      soaAgentNpn: selectedAgent.getNpn() ? selectedAgent.getNpn().getNpn() : '',
      //soaAgentSignature : selectedAgent.getUserFirstname()+' '+selectedAgent.getUserLastname(),
      soaInitialContactMethod: 'Phone',
      soaAppointmentDate: appointmentDate,
      soaDiscussedPlans: 'All of the above',
      soaDiscussionCheck: ['MAPD', 'PDP', 'SUP', 'DVH', 'ANC'],
      soaTimezoneOffset: visitortimezone,
      soaLanguage: 'ENG',
      isConsent: formTypeSelected == 'consentA' ? 1 : formTypeSelected == 'consentB' ? 2 : '',
      soaIncomeYear: new Date().getFullYear(),
      soaReviewDate: reviewDate,
      soaTransmissionMethod: null,
      soaIncome: individual.getSocial() ? individual.getSocial().getHouseholdIncome() : 0,
      consentCompany: newestPolicy ? newestPolicy.getCompany().getCompanyName() : '',
      consentEffective: newestPolicy
        ? newestPolicy.getPolicyEffective().split('/')[2] +
          '-' +
          newestPolicy.getPolicyEffective().split('/')[0] +
          '-' +
          newestPolicy.getPolicyEffective().split('/')[1]
        : '',
      consentPlan: newestPolicy ? newestPolicy.getPlan().getPlanName() : '',
      consentSubsidy: newestPolicy ? newestPolicy.getPolicySubsidy() : '',
      consentPremium: newestPolicy ? newestPolicy.getPolicyPremium() : '',
      user: selectedAgent.getUserId(),
      soaNoAuth: localStorage.getItem('soaNoAuth') ? true : false,
    };

    setFormData(formData);
    setInd(individual);
    //setAgents(agents);
    setSelectedAgent(selectedAgent);
  };

  /**
   * Handles fetching the user by ID
   * @param {number} userId
   * @param {Array} include
   * @returns {Promise<User>}
   */
  const handleFetchUser = (userId, include) => {
    return userRepository
      .getUser(userId, { include: include })
      .then((response) => {
        return response;
      })
      .catch((e) => {
        return e;
      });
  };

  /**
   * Handles fetching agency LOAS
   * @param {number} userId
   * @param {number} agencyId
   * @param {Array} include
   * @returns {Promise<User>}
   */
  const handleFetchUsers = (userId, agencyId, include) => {
    return agencyDownlineRepository
      .getAgencyLoasAndPrincipal(userId, agencyId, { include: include })
      .then((response) => {
        return response;
      })
      .catch((e) => {
        return e;
      });
  };

  const handleTogglePolicyDataOnConsentB = (includePolicyData) => {
    const _formData = { ...formData };
    if (includePolicyData) {
      _formData.consentCompany = newestPolicy ? newestPolicy.getCompany().getCompanyName() : '';
      _formData.consentEffective = newestPolicy
        ? newestPolicy.getPolicyEffective().split('/')[2] +
          '-' +
          newestPolicy.getPolicyEffective().split('/')[0] +
          '-' +
          newestPolicy.getPolicyEffective().split('/')[1]
        : '';
      _formData.consentPlan = newestPolicy ? newestPolicy.getPlan().getPlanName() : '';
      _formData.consentSubsidy = newestPolicy ? newestPolicy.getPolicySubsidy() : '';
      _formData.consentPremium = newestPolicy ? newestPolicy.getPolicyPremium() : '';
    } else {
      _formData.consentCompany = '';
      _formData.consentEffective = '';
      _formData.consentPlan = '';
      _formData.consentSubsidy = '';
      _formData.consentPremium = '';
    }

    setFormData(_formData);
  };

  /**
   * Handles fetching the correct agent
   * @param {Array<User>} agents
   * @param {number} userId
   * @param {Array} include
   * @returns {Promise<User>}
   */
  const handleFetchAgent = (agents, userId, include) => {
    const fetchAgent = new Promise((resolve) => {
      const selectedAgent = agents.find((agent) => agent.getUserId() === userId);

      if (selectedAgent) {
        resolve(selectedAgent);
      } else {
        setAgents(agents);
        resolve(agents);
        /*handleFetchUser(userId, include).then(response => {     
                    agents.push(response);
                    setAgents(agents);
                    resolve(response);
                })*/
      }
    });

    return fetchAgent;
  };

  const handleDocUpload = async (indId: number, fileData: any) => {
    setUploadError(false);
    const response = await props.onDocUpload(indId, fileData);

    if (!response) {
      setUploadError(true);
    } else {
      const _formData = { ...formData, ['soaExternalSrc']: fileData.name };
      setFormData(_formData);
      setFormDocAttachment(response);
    }

    return response;
  };

  /**
   * Handles fetching individual and user on form start
   */
  React.useEffect(() => {
    if (props.props.indId) {
      const individualRepository = new IndRepository();
      individualRepository.setAccessToken(props.props.userAccessToken);
      const include: Array<IndAPIInclude> = [
        'addresses',
        'phones',
        'spouse',
        'emails',
        'loa',
        'owner',
      ];

      if (formTypeSelected === 'consentB') {
        include.push('policies');
        include.push('policies.company');
        include.push('policies.plan');
        include.push('social');
      }

      individualRepository
        .getInd(props.props.indId, { include: include })
        .then(async (individual) => {
          const agId = props.props.directUplineId ?? props.props.agencyId ?? null;
          // If agency get all the available agents
          let selectedAgent = null;
          if (agId) {
            try {
              const agents = await handleFetchUsers(props.props.userId, agId, include);

              const agentObjects = agents.getItems().map((item) => item.getUser());

              selectedAgent = agentObjects.find((item) => {
                return item.getUserId() == props.props.userId;
              });

              if (selectedAgent) {
                setAgents(agentObjects);
              }

              if (!selectedAgent) {
                selectedAgent = individual.getOwnerId();
              }
            } catch (e) {
              selectedAgent = individual.getOwnerId();
            }
          } else {
            selectedAgent = individual.getOwnerId();
          }

          // get newest IND plan
          let newestPolicy = null;

          if (formTypeSelected === 'consentB') {
            newestPolicy = individual.getMostRecentPolicyByType('IND');
            if (newestPolicy) {
              setNewestPolicy(newestPolicy);
            }
          }

          handleMakeFormData(individual, selectedAgent, newestPolicy);
        });
    }
  }, []);

  return (
    <>
      <StyledModal
        size="lg"
        title={typeLegend.find((type) => type.value == formTypeSelected).label}
        footerCloseButton={true}
        show={true}
        onClose={props.onClose}
      >
        {formData && individual && (
          <>
            <div className="row gutter-2 fusion-ignore">
              {formTypeSelected !== 'manual' && (
                <div className="col-12 hiddenStuff">
                  <div className="alert alert-info p1 " style={{ fontSize: '11pt' }}>
                    <i className="fal fa-comment mr-2"></i>Please start by completing the agent
                    portion of the SOA. The beneficiary will receive a link to complete their
                    required fields.
                  </div>
                </div>
              )}

              {agents !== undefined && agents.length > 1 && selectedAgent && (
                <>
                  <div className="col-12 p-2 font-weight-bold mb-2 alternateSoaAgent boldHeaderLabel">
                    Agent
                  </div>

                  <div className="col-12 col-md-12 mb-2 calMess alternateSoaAgent">
                    <select
                      className="form-control form-control-sm soaAgent"
                      onChange={handleAgentChange}
                    >
                      {agents.map((agent, index) => {
                        return (
                          <option
                            key={index}
                            selected={agent.getUserId() === selectedAgent.getUserId()}
                            value={index}
                          >
                            {agent.getUserFirstname() + ' ' + agent.getUserLastname()}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </>
              )}

              <div className="col-12 p-2 font-weight-bold mb-2 boldHeaderLabel">Language</div>
              <div className="col-12 col-md-12 mb-2 calMess">
                <select
                  className="form-control form-control-sm"
                  name="soaLanguage"
                  onChange={handleFormChange}
                >
                  {languageOptions.map((language, index) => {
                    return (
                      <option
                        key={index}
                        selected={formData.soaLanguage == language.value ? true : false}
                        value={language.value}
                      >
                        {language.label}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {formTypeSelected == 'soa' && (
              <SoaForm formData={formData} onChange={handleFormChange} />
            )}

            {formTypeSelected == 'consentA' && (
              <ConsentAForm formData={formData} onChange={handleFormChange} />
            )}

            {formTypeSelected == 'consentB' && (
              <ConsentBForm
                formData={formData}
                onChange={handleFormChange}
                onPolicyToggle={handleTogglePolicyDataOnConsentB}
              />
            )}
            {formTypeSelected == 'manual' && (
              <Manual
                formData={formData}
                onChange={handleFormChange}
                userId={props.props.userId}
                indId={props.props.indId}
                onDocUpload={handleDocUpload}
              />
            )}

            {formDocAttachment && (
              <div
                className="calMess fileInstance p-2 mb-2 rounded font-weight-bold"
                style={{ border: '2px dotted #999999', fontSize: '10pt' }}
              >
                <i className="far fa-file-import mr-2 "></i>
                <span>{formDocAttachment.getIndDocName()}</span>
                <span
                  className="badge badge-secondary badge-pill ml-2 cp"
                  style={{ fontSize: '8pt' }}
                  onClick={() => setFormDocAttachment(null)}
                >
                  <i className="far fa-times"></i> Remove
                </span>
              </div>
            )}
            {uploadError && (
              <div className="text-danger">There was an issue uploading your document</div>
            )}

            {sendFailureMessage == null && !formWasCreated && formTypeSelected !== 'manual' && (
              <>
                <div className="col-12 p-2 font-weight-bold border-top border-info mt-3 mb-2 boldHeaderLabel">
                  How do you want to send the{' '}
                  {typeLegend.find((type) => type.value == formTypeSelected).label}?
                </div>
                <div className="col-12">
                  {sendingOptions.map((sendOption, index) => {
                    return (
                      <div className="form-check">
                        <input
                          key={index}
                          className="form-check-input"
                          type="radio"
                          data-id="text"
                          value={sendOption.value}
                          name="soaTransmissionMethod"
                          checked={formData.soaTransmissionMethod === sendOption.value}
                          onChange={handleFormChange}
                        />
                        <label className="form-check-label">{sendOption.label}</label>
                      </div>
                    );
                  })}
                </div>
                {['email', 'sms'].includes(formData.soaTransmissionMethod) && (
                  <div className="col-12 mt-3">
                    <input
                      type="checkbox"
                      name="soaNoAuth"
                      checked={!formData.soaNoAuth}
                      onChange={handleFormChange}
                    />{' '}
                    <span>Require auth code to access document?</span>
                    {!formData.soaNoAuth && (
                      <div className="alert alert-info mt-2 p-1" style={{ fontSize: '9pt' }}>
                        <i className="far fa-info-circle mr-1"></i>Beneficiary will receive a
                        communication with an access code and the link to access the document.
                      </div>
                    )}
                  </div>
                )}
                <div className="col-12 mt-2">
                  {formData.soaTransmissionMethod === 'email' && (
                    <UserSendEmailSelection
                      props={props.props}
                      ind={individual}
                      user={selectedAgent}
                      onChange={handleSendingMethodSelections}
                      isValid={handleSendingMethodIsValid}
                      onIntegrationSetupClick={handleIntegrationSetup}
                    />
                  )}

                  {formData.soaTransmissionMethod === 'sms' && (
                    <UserSendSmsSelection
                      props={props.props}
                      ind={individual}
                      user={selectedAgent}
                      onChange={handleSendingMethodSelections}
                      isValid={handleSendingMethodIsValid}
                    />
                  )}
                </div>
              </>
            )}
            <div className="row gutter-2 fusion-ignore">
              <div className="col-12 mt-2">
                {sendingMethodIsValid &&
                  handleFormValidation() &&
                  !sendFailureMessage &&
                  !formWasCreated && (
                    <button
                      disabled={handleFormValidation() === false}
                      onClick={() => handleFormSave(formData)}
                      type="button"
                      className="btn btn-sm border border-success btn-success mt-3"
                    >
                      {!formIsSaving && <>Create Form</>}

                      {formIsSaving && (
                        <>
                          <i className="far fa-spinner fa-spin mr-1"></i>Creating...
                        </>
                      )}
                    </button>
                  )}
              </div>
              {sendFailureMessage !== null && (
                <div className="col-12 mt-2">
                  <div className="alert alert-warning p-2 mt-2" style={{ fontSize: '9pt' }}>
                    <b>Form was created but unable to send</b>
                    <br></br>
                    {sendFailureMessage}
                  </div>
                </div>
              )}

              {sendFailureMessage == null && formWasCreated && (
                <div className="col-12 mt-2">
                  <div className="alert alert-success p-2 mt-2" style={{ fontSize: '9pt' }}>
                    <b>
                      <i className="far fa-check-circle mr-1"></i>Form created! You can close this
                      modal.
                    </b>
                    <br></br>
                    {sendFailureMessage}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {(!formData || !individual) && <LoaderWheel />}
      </StyledModal>
    </>
  );
};
